import React, { Component, ReactNode } from 'react';
import { AlertTriangle, RefreshCw, Home, ArrowLeft } from 'lucide-react';

interface ErrorBoundaryProps {
  children: ReactNode;
  fallbackContent?: string;
  showHomeButton?: boolean;
  className?: string;
  onError?: (error: Error, errorInfo: React.ErrorInfo) => void;
}

interface ErrorBoundaryState {
  hasError: boolean;
  error?: Error;
  errorInfo?: React.ErrorInfo;
  retryCount: number;
}

class ErrorBoundary extends Component<ErrorBoundaryProps, ErrorBoundaryState> {
  constructor(props: ErrorBoundaryProps) {
    super(props);
    this.state = {
      hasError: false,
      retryCount: 0
    };
  }

  static getDerivedStateFromError(error: Error): Partial<ErrorBoundaryState> {
    return {
      hasError: true,
      error
    };
  }

  componentDidCatch(error: Error, errorInfo: React.ErrorInfo) {
    this.setState({
      error,
      errorInfo
    });
    
    // 调用外部错误处理函数
    if (this.props.onError) {
      this.props.onError(error, errorInfo);
    }
    
    // 在开发环境中打印错误信息
    if (import.meta.env.DEV) {
      console.error('ErrorBoundary caught an error:', error, errorInfo);
    }
  }

  handleRetry = () => {
    this.setState(prevState => ({
      hasError: false,
      error: undefined,
      errorInfo: undefined,
      retryCount: prevState.retryCount + 1
    }));
  };

  getErrorIcon = () => {
    const { error } = this.state;
    if (!error) return '⚠️';
    
    // 根据错误类型返回不同图标
    if (error.message.includes('Network') || error.message.includes('fetch')) {
      return '🌐';
    }
    if (error.message.includes('auth') || error.message.includes('Auth')) {
      return '🔐';
    }
    return '⚠️';
  };

  getErrorTitle = () => {
    const { error } = this.state;
    if (!error) return '出现了一些问题';
    
    if (error.message.includes('Network') || error.message.includes('fetch')) {
      return '网络连接问题';
    }
    if (error.message.includes('auth') || error.message.includes('Auth')) {
      return '身份验证错误';
    }
    return '出现了一些问题';
  };

  getErrorDescription = () => {
    const { error } = this.state;
    const { fallbackContent } = this.props;
    
    if (!error) {
      return fallbackContent || '抱歉，页面出现了问题，请稍后重试。';
    }
    
    if (error.message.includes('Network') || error.message.includes('fetch')) {
      return '请检查您的网络连接，然后重试。';
    }
    if (error.message.includes('auth') || error.message.includes('Auth')) {
      return '身份验证失败，请重新登录。';
    }
    
    return fallbackContent || '抱歉，页面出现了问题，请稍后重试。';
  };

  getSuggestions = () => {
    const { error } = this.state;
    
    if (!error) {
      return ['刷新页面', '稍后再试', '联系技术支持'];
    }
    
    if (error.message.includes('Network') || error.message.includes('fetch')) {
      return [
        '检查网络连接',
        '刷新页面重试',
        '稍后再试'
      ];
    }
    
    if (error.message.includes('auth') || error.message.includes('Auth')) {
      return [
        '重新登录',
        '清除浏览器缓存',
        '联系管理员'
      ];
    }
    
    return [
      '刷新页面',
      '稍后再试',
      '联系技术支持'
    ];
  };

  render() {
    if (!this.state.hasError) {
      return this.props.children;
    }

    const { showHomeButton = true, className = '' } = this.props;
    const { error, retryCount } = this.state;

    return (
      <div className={`min-h-screen bg-gradient-to-br from-blue-50 to-indigo-100 flex items-center justify-center p-4 ${className}`}>
        <div className="max-w-md w-full bg-white rounded-2xl shadow-xl p-8 text-center">
          {/* 错误图标 */}
          <div className="mb-6">
            <div className="w-20 h-20 mx-auto bg-red-100 rounded-full flex items-center justify-center text-4xl mb-4">
              {this.getErrorIcon()}
            </div>
            <AlertTriangle className="w-8 h-8 text-red-500 mx-auto" />
          </div>

          {/* 错误标题 */}
          <h1 className="text-2xl font-bold text-gray-900 mb-4">
            {this.getErrorTitle()}
          </h1>

          {/* 错误描述 */}
          <p className="text-gray-600 mb-6 leading-relaxed">
            {this.getErrorDescription()}
          </p>

          {/* 建议列表 */}
          <div className="mb-8">
            <h3 className="text-sm font-semibold text-gray-700 mb-3">建议解决方案：</h3>
            <ul className="text-sm text-gray-600 space-y-2">
              {this.getSuggestions().map((suggestion, index) => (
                <li key={index} className="flex items-center justify-center">
                  <span className="w-1.5 h-1.5 bg-blue-400 rounded-full mr-2"></span>
                  {suggestion}
                </li>
              ))}
            </ul>
          </div>

          {/* 操作按钮 */}
          <div className="space-y-3">
            <button
              onClick={this.handleRetry}
              className="w-full bg-blue-600 hover:bg-blue-700 text-white font-medium py-3 px-6 rounded-lg transition-colors duration-200 flex items-center justify-center space-x-2"
            >
              <RefreshCw className="w-4 h-4" />
              <span>重试 {retryCount > 0 && `(${retryCount})`}</span>
            </button>

            {showHomeButton && (
              <button
                onClick={() => window.location.href = '/'}
                className="w-full bg-gray-100 hover:bg-gray-200 text-gray-700 font-medium py-3 px-6 rounded-lg transition-colors duration-200 flex items-center justify-center space-x-2"
              >
                <Home className="w-4 h-4" />
                <span>返回首页</span>
              </button>
            )}
          </div>

          {/* 错误详情（仅开发环境） */}
          {import.meta.env.DEV && error && (
            <details className="mt-6 text-left">
              <summary className="cursor-pointer text-sm text-gray-500 hover:text-gray-700">
                错误详情 (开发模式)
              </summary>
              <div className="mt-2 p-3 bg-gray-50 rounded text-xs text-gray-600 font-mono overflow-auto max-h-32">
                <div className="mb-2">
                  <strong>错误信息:</strong> {error.message}
                </div>
                <div className="mb-2">
                  <strong>错误堆栈:</strong>
                  <pre className="whitespace-pre-wrap">{error.stack}</pre>
                </div>
                {this.state.errorInfo && (
                  <div>
                    <strong>组件堆栈:</strong>
                    <pre className="whitespace-pre-wrap">{this.state.errorInfo.componentStack}</pre>
                  </div>
                )}
              </div>
            </details>
          )}
        </div>
      </div>
    );
  }
}

export default ErrorBoundary;